import React, { useEffect, useRef, useState } from "react"
import profileIcon from "../../asset/profile_icon.png"
import LogoutButton from "../components/button/LogoutButton"
import { container } from "../../infrastructure/container"
import { SessionService } from "../../infrastructure/services/session"
import { FranchiseRole } from "../../infrastructure/models/enums/franchiserole"
import { useNavigate } from "react-router-dom"

const ProfileMenu: React.FC = () => {
  const sessionService = container.resolve<SessionService>("sessionService")
  const user = sessionService.user

  const [showProfileMenu, setShowProfileMenu] = useState<boolean>(false)
  const navigate = useNavigate()

  const profileMenuRef = useRef<HTMLDivElement>(null)
  const [phoneNumber, setPhoneNumber] = useState<string>("")

  useEffect(() => {
    /**
     * Fetches the stored phone number from localStorage when the component mounts.
     */
    if (user) {
      setPhoneNumber(user.phone.primary)
    }
  }, [user])

  useEffect(() => {
    /**
     * Handles clicking outside the profile menu to close it.
     * @param {MouseEvent} event - The click event.
     */
    const handleClickOutside = (event: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setShowProfileMenu(false)
      }
    }

    if (showProfileMenu) {
      document.addEventListener("mousedown", handleClickOutside)
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [showProfileMenu])

  const toggleProfileMenu = (event: React.MouseEvent) => {
    event.stopPropagation()
    setShowProfileMenu((prev) => !prev)
  }

  const handleMyAccountClick = () => {
    setShowProfileMenu(false)
    navigate("/my-account")
  }

  /**
   * Gets the role name from the FranchiseRole enum using reverse mapping.
   * @param role - The role value (e.g., 0, 1, 2).
   * @returns The role name as a string (e.g., "OWNER", "ADMIN").
   */
  const getRoleName = (role: FranchiseRole): string => {
    return FranchiseRole[role]
  }

  return (
    <>
      <div className="relative z-50">
        <div className="flex items-center space-x-2 md:space-x-3 p-1 md:p-2">
          <div className="relative">
            <img
              data-automation-id="profilemenu-user-profile-icon-img"
              id="profilemenu-user-profile-icon-img"
              src={user?.profileImage || profileIcon}
              alt="Profile"
              className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 rounded-full cursor-pointer object-cover"
              onClick={toggleProfileMenu}
            />
            {/* Active indicator */}
            {showProfileMenu && <div className="absolute inset-0 rounded-full border-2 border-blue-500"></div>}
          </div>
          <div className="leading-tight hidden sm:block">
            <p
              data-automation-id="profilemenu-user-details-first-last-name"
              id="profilemenu-user-details-first-last-name"
              className="font-bold text-gray-900 text-sm md:text-base whitespace-nowrap"
            >
              {user?.firstname || "User"} {user?.lastname || ""}
            </p>
            <p
              data-automation-id="profilemenu-user-details-role"
              id="profilemenu-user-details-role"
              className="text-xs md:text-sm text-gray-500"
            >
              {getRoleName(user?.role)}
            </p>
          </div>
        </div>

        {showProfileMenu && (
          <div
            ref={profileMenuRef}
            className="absolute right-0 mt-2 w-48 sm:w-56 bg-white shadow-lg rounded-lg p-3 z-50"
          >
            <div className="space-y-2">
              <div className="flex items-center space-x-3">
                <img
                  data-automation-id="profilemenu-logged-in-user-img"
                  id="profilemenu-logged-in-user-img"
                  src={user?.profileImage || profileIcon}
                  alt="Profile"
                  className="w-10 h-10 rounded-full"
                />
                <div>
                  <p className="font-semibold text-sm md:text-base truncate">
                    {user?.firstname} {user?.lastname}
                  </p>
                  <p className="text-xs text-gray-500">{getRoleName(user?.role)}</p>
                </div>
              </div>
              <div className="pt-2 border-t border-gray-100">
                <p className="text-xs md:text-sm break-words">{user?.email || "No Email"}</p>
                <p className="text-xs md:text-sm mt-1">{phoneNumber}</p>
              </div>
              <button
                onClick={handleMyAccountClick}
                className="w-full text-left px-2 py-2 text-xs md:text-sm text-gray-700 hover:bg-gray-100 rounded transition-colors"
              >
                My Account
              </button>
              <LogoutButton
                data-automation-id="profilemenu-logout-portal-button"
                id="profilemenu-logout-portal-button"
                className="mt-2 md:mt-3 text-red-500 hover:text-red-700 text-xs font-medium"
              />
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default ProfileMenu